type MaintenanceStory = {
  Tag: string;
  Title: string;
  Content: string;
  ReleaseDate: string;
  ReleaseEnd: string;
  Deployed: boolean;
};

 /** 
  * Equiv to around 25 days. Safeguard for potential overflows.
  * Maintenance window will always be less than this value for internal policies.
  */
const MAX_TIMEOUT_DELAY_MS = 2 ** 31 - 1;

function coerceBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
  }
  if (typeof value === "number") return value !== 0;
  return Boolean(value);
}

function parseLocalDateTime(input: string): Date | null {
  const trimmed = (input ?? "").trim();
  if (!trimmed) return null;

  const normalized = trimmed.split("T").join(" ");
  const dateTimeParts = normalized.split(" ").filter(part => part.length > 0);
  if (dateTimeParts.length === 0 || dateTimeParts.length > 2) return null;

  const [datePart, timePart] = dateTimeParts;
  const calendarPieces = datePart.split("-");
  if (calendarPieces.length !== 3) return null;

  const [yearValue, monthValue, dayValue] = calendarPieces.map(piece => piece.trim());

  const hasTime = Boolean(timePart);
  const timePieces = hasTime ? timePart.split(":") : ["00", "00", "00"];
  if (timePieces.length < 2 || timePieces.length > 3) return null;
  const [hourValue, minuteValue, secondValue = "0"] = timePieces.map(piece => piece.trim());

  const year = parseInteger(yearValue);
  const month = parseInteger(monthValue);
  const day = parseInteger(dayValue);
  const hour = parseInteger(hourValue);
  const minute = parseInteger(minuteValue);
  const second = parseInteger(secondValue);

  if (year === null || month === null || day === null || hour === null || minute === null || second === null) {
    return null;
  }

  if (!isInRange(month, 1, 12)) return null;
  if (!isInRange(day, 1, 31)) return null;
  if (!isInRange(hour, 0, 23)) return null;
  if (!isInRange(minute, 0, 59)) return null;
  if (!isInRange(second, 0, 59)) return null;

  const date = new Date(year, month - 1, day, hour, minute, second, 0);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute ||
    date.getSeconds() !== second
  ) {
    return null;
  }

  return date;
}

function toMaintenanceObject(input: string): MaintenanceStory | undefined {
  try {
    const obj = JSON.parse(input) as MaintenanceStory;

    if (!obj || typeof obj !== "object") return undefined;

    const pickString = (a: unknown): string => (typeof a === "string" ? a : "");
    const pickBoolean = (a: unknown): boolean => coerceBoolean(a);

    const mapped: MaintenanceStory = {
      Tag: pickString(obj.Tag),
      Title: pickString(obj.Title),
      Content: pickString(obj.Content),
      ReleaseDate: pickString(obj.ReleaseDate),
      ReleaseEnd: pickString(obj.ReleaseEnd),
      Deployed: pickBoolean(obj.Deployed),
    };

    if (!mapped.ReleaseDate || !mapped.ReleaseEnd) return undefined;
    return mapped;
  } catch {
    return undefined;
  }
}

function parseMaintenance(maintenance: string): MaintenanceStory | undefined {
  try {
    return toMaintenanceObject(maintenance);
  } catch {
    return undefined;
  }
}

function computeIsInMaintenanceRange(maintenance?: MaintenanceStory, now: Date = new Date()): boolean {
  try {
    if (!maintenance) return false;
    if (maintenance.Deployed) return false;

    const startDate = parseLocalDateTime(maintenance.ReleaseDate);
    const endDate = parseLocalDateTime(maintenance.ReleaseEnd);
    if (!startDate || !endDate) return false;

    return now >= startDate && now < endDate;
  } catch {
    return false;
  }
}

function computeIsPlannedWithinWindowDays(maintenance: MaintenanceStory | undefined, windowDays: number): boolean {
  if (!maintenance) return false;
  if (maintenance.Deployed) return false;

  const startDate = parseLocalDateTime(maintenance.ReleaseDate);
  if (!startDate) return false;

  const now = new Date();
  const windowEnd = new Date(now.getTime() + windowDays * 24 * 60 * 60 * 1000);

  if (startDate <= now) return false;

  return startDate <= windowEnd;
}

function getMaintenanceReleaseDurationHours(maintenance: MaintenanceStory | undefined): number | undefined {
  if (!maintenance) return undefined;

  const startDate = parseLocalDateTime(maintenance.ReleaseDate);
  const endDate = parseLocalDateTime(maintenance.ReleaseEnd);
  if (!startDate || !endDate) return undefined;

  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs <= 0) return undefined;

  return Math.round(diffMs / (1000 * 60 * 60));
}

export {
  type MaintenanceStory,
  MAX_TIMEOUT_DELAY_MS,
  computeIsInMaintenanceRange,
  computeIsPlannedWithinWindowDays,
  getMaintenanceReleaseDurationHours,
  parseLocalDateTime,
  parseMaintenance,
};

function parseInteger(value: string): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return null;
  }
  return parsed;
}

function isInRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}
